import React, { useState, useEffect } from 'react';

interface TopByClicks {
  slug: string;
  clicks: number;
  badge: string;
}

interface TopByGeoHits {
  slug: string;
  geoHits: number;
}

interface DecayingPiece {
  slug: string;
  dropPct: number;
}

interface ClusterRow {
  cluster: string;
  totalClicks: number;
  totalGeoHits: number;
}

interface PerformanceSummary {
  topByClicks: TopByClicks[];
  topByGeoHits: TopByGeoHits[];
  decaying: DecayingPiece[];
  clusterRollup: ClusterRow[];
}

interface BrandPerformanceTabProps {
  brand: string;
}

/**
 * #228: the summary endpoint returns a row per piece even when nothing has been
 * synced (clicks/GEO all 0), so the tab rendered tables full of zeros instead of
 * explaining WHY. True when every metric is zero/empty → show the "not synced"
 * guidance instead of silent zeros. Exported for tests.
 */
export function isAllZeroSummary(s: PerformanceSummary): boolean {
  // No pieces at all is "empty brand", not "synced-but-zero" — don't mislabel it
  // (also avoids `[].every() === true` vacuously firing the banner).
  if (s.topByClicks.length === 0 && s.clusterRollup.length === 0) return false;
  return s.topByClicks.every((r) => r.clicks === 0)
    && s.topByGeoHits.length === 0
    && s.decaying.length === 0
    && s.clusterRollup.every((c) => c.totalClicks === 0 && c.totalGeoHits === 0);
}

function badgeEmoji(badge: string): string {
  if (badge === 'trending') return '🔥'; // fire emoji
  if (badge === 'decaying') return '⚠'; // warning triangle
  return '✓'; // check mark
}

const TABLE_HEADER: React.CSSProperties = {
  background: 'var(--bg-surface)',
  color: 'var(--text-tertiary)',
  fontSize: 'var(--fs-eyebrow)',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  padding: '8px 14px',
  textAlign: 'left',
};

const TABLE_CELL: React.CSSProperties = {
  padding: '9px 14px',
  fontSize: 'var(--fs-small)',
  color: 'var(--text-secondary)',
  borderBottom: '1px solid var(--border)',
};

const TABLE_CELL_PRIMARY: React.CSSProperties = {
  ...TABLE_CELL,
  color: 'var(--text-primary)',
  fontWeight: 600,
};

const SECTION_TITLE: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 700,
  color: 'var(--text-primary)',
  marginBottom: 12,
  marginTop: 0,
};

const TABLE_WRAPPER: React.CSSProperties = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border)',
  borderRadius: 0,
  overflow: 'hidden',
  marginBottom: 28,
};

const DIVIDER: React.CSSProperties = {
  borderTop: '1px solid var(--border)',
  marginBottom: 24,
};

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <div style={SECTION_TITLE}>{children}</div>;
}

function EmptyState({ message }: { message: string }) {
  return (
    <div style={{ color: 'var(--text-tertiary)', fontSize: 'var(--fs-small)', fontStyle: 'italic', marginBottom: 28 }}>
      {message}
    </div>
  );
}

export default function BrandPerformanceTab({ brand }: BrandPerformanceTabProps) {
  const [summary, setSummary] = useState<PerformanceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSummary(null);
    setLoading(true);
    setError(null);

    fetch(`/api/${brand}/performance/summary`)
      .then((r) => {
        if (!r.ok) return r.json().then((d: { error?: string }) => { throw new Error(d.error || `HTTP ${r.status}`); });
        return r.json();
      })
      .then((d: PerformanceSummary) => {
        setSummary(d);
        setLoading(false);
      })
      .catch((err: Error) => {
        setError(err.message);
        setLoading(false);
      });
  }, [brand]);

  if (loading) {
    return (
      <div style={{ padding: '24px 0', color: 'var(--text-tertiary)', fontSize: 'var(--fs-small)' }}>
        Loading performance summary...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px 0', color: 'var(--danger)', fontSize: 'var(--fs-small)' }}>
        Error: {error}
      </div>
    );
  }

  if (!summary) {
    return (
      <div style={{ padding: '24px 0', color: 'var(--text-tertiary)', fontSize: 'var(--fs-small)', fontStyle: 'italic' }}>
        No data yet.
      </div>
    );
  }

  // #228: all metrics zero → integrations almost certainly aren't synced yet.
  // Explain it instead of rendering tables full of zeros.
  if (isAllZeroSummary(summary)) {
    return (
      <div style={{ padding: '24px 0', maxWidth: 760 }}>
        <div role="status" style={{ padding: '16px 18px', background: 'var(--bg-warning, var(--warning-light))', border: '1px solid var(--border-warning, var(--warning))', borderRadius: 0, fontSize: 'var(--fs-small)', lineHeight: 1.6, color: 'var(--text-primary)' }}>
          <strong>No observability data synced yet.</strong> Every metric is zero — this deployment likely has no observability data ingested (or the integration secrets aren't set).
          <ul style={{ margin: '8px 0 0', paddingLeft: 18, color: 'var(--text-secondary)' }}>
            <li>Configure the observability secrets on the server: <code>GOOGLE_SERVICE_ACCOUNT_JSON</code> (GSC), <code>DEVTO_API_KEY</code> / <code>HASHNODE_TOKEN</code> (platform stats).</li>
            <li>Then open a piece's <strong>Performance</strong> tab and run <strong>Sync GSC</strong> / <strong>Sync now</strong> / <strong>Run probe</strong> to populate this view.</li>
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 0', maxWidth: 760 }}>

      {/* Top 10 by Clicks */}
      <SectionTitle>Top 10 by Clicks</SectionTitle>
      {summary.topByClicks.length === 0 ? (
        <EmptyState message="No data yet — sync observability to populate." />
      ) : (
        <div style={TABLE_WRAPPER}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={TABLE_HEADER}>Slug</th>
                <th style={{ ...TABLE_HEADER, textAlign: 'right' }}>Clicks</th>
                <th style={{ ...TABLE_HEADER, textAlign: 'center', width: 80 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {summary.topByClicks.map((row) => (
                <tr key={row.slug}>
                  <td style={TABLE_CELL_PRIMARY}>{row.slug}</td>
                  <td style={{ ...TABLE_CELL, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {row.clicks.toLocaleString()}
                  </td>
                  <td style={{ ...TABLE_CELL, textAlign: 'center' }} title={row.badge}>
                    {badgeEmoji(row.badge)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={DIVIDER} />

      {/* Top 10 by AI Citations */}
      <SectionTitle>Top 10 by AI Citations</SectionTitle>
      {summary.topByGeoHits.length === 0 ? (
        <EmptyState message="No GEO citation hits recorded yet." />
      ) : (
        <div style={TABLE_WRAPPER}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={TABLE_HEADER}>Slug</th>
                <th style={{ ...TABLE_HEADER, textAlign: 'right' }}>GEO Hits</th>
              </tr>
            </thead>
            <tbody>
              {summary.topByGeoHits.map((row) => (
                <tr key={row.slug}>
                  <td style={TABLE_CELL_PRIMARY}>{row.slug}</td>
                  <td style={{ ...TABLE_CELL, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {row.geoHits}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={DIVIDER} />

      {/* Needs Refresh — decaying pieces */}
      <SectionTitle>Needs Refresh</SectionTitle>
      {summary.decaying.length === 0 ? (
        <EmptyState message="No decaying pieces detected." />
      ) : (
        <div style={TABLE_WRAPPER}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={TABLE_HEADER}>Slug</th>
                <th style={{ ...TABLE_HEADER, textAlign: 'right' }}>Click drop</th>
              </tr>
            </thead>
            <tbody>
              {summary.decaying.map((row) => (
                <tr key={row.slug}>
                  <td style={{ ...TABLE_CELL_PRIMARY, color: 'var(--danger)' }}>{row.slug}</td>
                  <td style={{ ...TABLE_CELL, textAlign: 'right', color: 'var(--danger)', fontWeight: 700 }}>
                    {row.dropPct}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={DIVIDER} />

      {/* By Topic Cluster */}
      <SectionTitle>By Topic Cluster</SectionTitle>
      {summary.clusterRollup.length === 0 ? (
        <EmptyState message="No cluster data — run orchestrate build-catalog first." />
      ) : (
        <div style={TABLE_WRAPPER}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={TABLE_HEADER}>Cluster</th>
                <th style={{ ...TABLE_HEADER, textAlign: 'right' }}>Clicks</th>
                <th style={{ ...TABLE_HEADER, textAlign: 'right' }}>AI Hits</th>
              </tr>
            </thead>
            <tbody>
              {summary.clusterRollup.map((row) => (
                <tr key={row.cluster}>
                  <td style={TABLE_CELL_PRIMARY}>{row.cluster}</td>
                  <td style={{ ...TABLE_CELL, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {row.totalClicks.toLocaleString()}
                  </td>
                  <td style={{ ...TABLE_CELL, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {row.totalGeoHits}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
}
